import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import attentionIllustration from "../assets/dashboard-attention.png";
import { fetchDashboardData } from "../services/dashboardService";
import "../styles/dashboard.css";
import { useAuth } from "../contexts/loginContext";
import Skeleton, { SkeletonStack } from "../components/UI/Skeleton";
import OnboardingTour from "../components/UI/OnboardingTour";
import CountUp from "../components/UI/CountUp";
import BoletasResumenCard from "../components/Boletas/BoletasResumenCard";
import { ROLES_BOLETAS } from "../contexts/roles";

import { Bar } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

// Animación de los gráficos: en la primera carga las barras crecen en cascada;
// después (clics, filtros) solo hay una transición suave sin retraso.
const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Se combina con `set` en lugar de reemplazar el objeto: Chart.js usa sus claves
// internas (type, fn, from, to) para interpolar colores; sin ellas falla con
// "this._fn is not a function" al pasar el mouse o cambiar colores.
Chart.defaults.set("animation", {
  duration: prefersReducedMotion ? 0 : 750,
  easing: "easeOutQuart",
  onComplete: ({ chart }) => {
    chart.$entradaAnimada = true;
  },
  delay: (ctx) =>
    !prefersReducedMotion &&
    ctx.type === "data" &&
    ctx.mode === "default" &&
    !ctx.chart.$entradaAnimada
      ? ctx.dataIndex * 70 + ctx.datasetIndex * 120
      : 0,
});

// Atenúa un color hex "#rrggbb"; los que ya traen transparencia se dejan igual.
const atenuar = (color) =>
  typeof color === "string" && /^#[0-9a-f]{6}$/i.test(color) ? `${color}55` : color;

// Al pasar el mouse sobre una barra (o fila, en los apilados), atenúa las demás.
const focoHoverPlugin = {
  id: "focoHover",
  beforeUpdate(chart) {
    // react-chartjs-2 reasigna los colores originales en cada render: se vuelven a envolver.
    chart.data.datasets.forEach((ds) => {
      if (typeof ds.backgroundColor === "function" && ds.backgroundColor.$foco) return;
      const base = ds.backgroundColor;
      const color = (ctx) => {
        const original = Array.isArray(base) ? base[ctx.dataIndex % base.length] : base;
        const foco = ctx.chart.$focoIndex;
        return foco == null || foco === ctx.dataIndex ? original : atenuar(original);
      };
      color.$foco = true;
      color.$base = base;
      ds.backgroundColor = color;
    });
  },
  afterEvent(chart, { event }) {
    if (event.type !== "mousemove" && event.type !== "mouseout") return;
    const activos = event.type === "mouseout" ? [] : chart.getActiveElements();
    const index = activos.length ? activos[0].index : null;
    if (chart.$focoIndex === index) return;
    chart.$focoIndex = index;
    chart.update("active");
  },
};

Chart.register(focoHoverPlugin);

// La leyenda toma el color de la primera barra, que puede estar atenuada (por hover o por
// selección): se fuerza el color sólido original de cada serie.
const generarEtiquetas = Chart.defaults.plugins.legend.labels.generateLabels;
Chart.defaults.plugins.legend.labels.generateLabels = (chart) =>
  generarEtiquetas(chart).map((item) => {
    const base = chart.data.datasets[item.datasetIndex]?.backgroundColor?.$base;
    const color = Array.isArray(base) ? base[0] : base;
    if (typeof color !== "string") return item;
    const solido = /^#[0-9a-f]{8}$/i.test(color) ? color.slice(0, 7) : color;
    return { ...item, fillStyle: solido, strokeStyle: solido };
  });

const MESES = [
  { v: 0, t: "Todos" },
  { v: 1, t: "Enero" },
  { v: 2, t: "Febrero" },
  { v: 3, t: "Marzo" },
  { v: 4, t: "Abril" },
  { v: 5, t: "Mayo" },
  { v: 6, t: "Junio" },
  { v: 7, t: "Julio" },
  { v: 8, t: "Agosto" },
  { v: 9, t: "Septiembre" },
  { v: 10, t: "Octubre" },
  { v: 11, t: "Noviembre" },
  { v: 12, t: "Diciembre" },
];

const DASHBOARD_TOUR = [
  { selector: ".dash-hero", title: "Tu panel de control", description: "Aquí tienes una vista rápida del portal: salidas a campo del personal y, según tu rol, boletas de pago." },
  { selector: ".dash-hero__actions", title: "Filtra el período", description: "Elige el mes y año para consultar la información que necesitas." },
  { selector: ".dash-attention", title: "Requiere atención", description: "Este bloque prioriza las solicitudes o registros que necesitan una decisión." },
  { selector: ".dash-kpi-grid", title: "Resumen del período", description: "Consulta totales, aprobaciones, rechazos, pendientes y registros completos." },
  { selector: '[data-onboarding="status-chart"]', title: "Estado de las solicitudes", description: "Compara rápidamente solicitudes pendientes, aprobadas y rechazadas." },
  { selector: '[data-onboarding="status-by-type-chart"]', title: "Estado por tipo de salida", description: "Compara, para cada tipo de salida, cuántas solicitudes se aprobaron, siguen pendientes o se rechazaron." },
  { selector: '[data-onboarding="quality-chart"]', title: "Calidad del registro de campo", description: "Muestra cuántas comisiones de servicio registraron su hora de fin y a cuántas les falta." },
  { selector: '[data-onboarding="destinations-chart"]', title: "Destinos frecuentes", description: "Identifica los destinos con mayor cantidad de solicitudes." },
  { selector: '[data-onboarding="people-chart"]', title: "Personas con más solicitudes", description: "Muestra quiénes registran más solicitudes de salida." },
  { selector: '[data-onboarding="managers-chart"]', title: "Solicitudes recibidas por responsable", description: "Cuántas solicitudes de su equipo recibió cada encargado y cuántas aprobó, rechazó o tiene pendientes." },
  { selector: '[data-onboarding="weekday-chart"]', title: "Día de la semana", description: "Identifica qué días concentran más salidas para planificar la carga del equipo." },
  { selector: '[data-onboarding="types-rank-chart"]', title: "Tipos de salida más usados", description: "Ranking de los tipos de salida o permiso con más solicitudes en el período." },
  { selector: '[data-onboarding="aging-chart"]', title: "¿Cuánto esperan las pendientes?", description: "Agrupa las solicitudes sin aprobar según los días que llevan esperando desde que se enviaron. Verde es reciente; rojo, muy atrasada. Atiende primero las de la derecha." },
];

const ANTIGUEDAD_COLORS = ["#22c55e", "#facc15", "#f59e0b", "#ef4444"];

const DIAS_LARGOS = {
  Lun: "el lunes",
  Mar: "el martes",
  Mié: "el miércoles",
  Jue: "el jueves",
  Vie: "el viernes",
  Sáb: "el sábado",
  Dom: "el domingo",
};

// Máximo de destinos en el gráfico: mantiene todas las tarjetas a la misma altura.
const MAX_DESTINOS_CHART = 8;

function SkeletonChartCard({ titleWidth = 220, bars = 6 }) {
  return (
    <div className="dash-section dash-skeleton__section">
      <div className="dash-section-header">
        <Skeleton className="dash-skeleton__section-title" height={18} width={titleWidth} radius={999} />
      </div>
      <div className="dash-chart-container dash-skeleton__chart">
        <div className="dash-skeleton__bars">
          {Array.from({ length: bars }).map((_, idx) => (
            <Skeleton
              key={idx}
              className="dash-skeleton__bar"
              height={14}
              width={`${90 - idx * 9}%`}
              radius={999}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="page dashboard-page" aria-busy="true" aria-live="polite">
      <div className="dash-skeleton">
        <div className="dash-hero">
          <div className="dash-hero__content dash-skeleton__hero-content">
            <Skeleton className="dash-skeleton__eyebrow" height={12} width={112} radius={999} />
            <Skeleton className="dash-skeleton__title" height={68} width="min(620px, 94%)" radius={22} />
            <SkeletonStack className="dash-skeleton__subtitle-stack" gap={10}>
              <Skeleton className="dash-skeleton__subtitle" height={20} width="min(720px, 100%)" radius={999} />
              <Skeleton className="dash-skeleton__subtitle" height={20} width="min(520px, 82%)" radius={999} />
            </SkeletonStack>
          </div>

          <div className="dash-hero__actions">
            <div className="filters-inline dash-skeleton__filters">
              <div className="period-field">
                <Skeleton className="dash-skeleton__label" height={11} width={42} radius={999} />
                <Skeleton className="dash-skeleton__control" height={42} width={142} radius={14} />
              </div>
              <div className="period-field">
                <Skeleton className="dash-skeleton__label" height={11} width={42} radius={999} />
                <Skeleton className="dash-skeleton__control" height={42} width={142} radius={14} />
              </div>
            </div>
          </div>
        </div>

        <section className="dash-block">
          <div className="dash-block-title-row">
            <Skeleton className="dash-skeleton__section-title" height={24} width={280} radius={999} />
          </div>

          <div className="dash-kpi-grid">
            {Array.from({ length: 5 }).map((_, idx) => (
              <div className="dash-kpi-card dash-skeleton__card" key={`kpi-${idx}`}>
                <Skeleton className="dash-skeleton__label" height={12} width={idx === 4 ? 170 : 120} radius={999} />
                <Skeleton className="dash-skeleton__value" height={32} width={idx === 4 ? 92 : 72} radius={14} />
                {idx === 4 ? (
                  <SkeletonStack className="dash-skeleton__mini-stack" gap={8}>
                    <Skeleton className="dash-skeleton__mini" height={13} width="58%" radius={999} />
                    <Skeleton className="dash-skeleton__pill" height={24} width={132} radius={999} />
                  </SkeletonStack>
                ) : null}
              </div>
            ))}
          </div>

          <div className="dash-chart-grid">
            <SkeletonChartCard titleWidth={200} bars={3} />
            <SkeletonChartCard titleWidth={170} bars={4} />
            <SkeletonChartCard titleWidth={190} bars={5} />
          </div>
        </section>

        <section className="dash-block">
          <div className="dash-block-title-row">
            <Skeleton className="dash-skeleton__section-title" height={24} width={240} radius={999} />
          </div>
          <div className="dash-chart-grid">
            <SkeletonChartCard titleWidth={190} />
            <SkeletonChartCard titleWidth={210} />
            <SkeletonChartCard titleWidth={230} />
          </div>
        </section>

        <section className="dash-block">
          <div className="dash-block-title-row">
            <Skeleton className="dash-skeleton__section-title" height={24} width={260} radius={999} />
          </div>
          <div className="dash-chart-grid">
            <SkeletonChartCard titleWidth={220} bars={5} />
            <SkeletonChartCard titleWidth={200} bars={5} />
            <SkeletonChartCard titleWidth={210} bars={4} />
          </div>
        </section>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { role, managerCode, user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [monthPermisos, setMonthPermisos] = useState(new Date().getMonth() + 1);
  const [yearPermisos, setYearPermisos] = useState(new Date().getFullYear());

  // Responsable elegido con clic en su barra; null = el que más solicitudes recibió.
  const [responsableSel, setResponsableSel] = useState(null);

  useEffect(() => {
    setResponsableSel(null);
  }, [data]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetchDashboardData({
          role,
          managerCode,
          monthPermisos: monthPermisos === 0 ? null : monthPermisos,
          yearPermisos,
        });
        setData(res);
      } catch (e) {
        console.error(e);
        setError("No se pudo cargar el dashboard.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [role, managerCode, monthPermisos, yearPermisos]);

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
    }),
    []
  );

  const calidadOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    }),
    []
  );

  const diaSemanaOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "bottom" } },
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
      },
    }),
    []
  );

  const destinosOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      layout: {
        padding: {
          left: 8,
          right: 8,
          top: 8,
          bottom: 8,
        },
      },
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0 } },
        y: { ticks: { autoSkip: false } },
      },
    }),
    []
  );

  const userRankOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "bottom" } },
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0 }, stacked: true },
        y: { stacked: true },
      },
    }),
    []
  );

  const estadoApiladoOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      // En barras horizontales el modo "index" debe buscar sobre el eje Y;
      // si no, toma la fila según la posición X del cursor y muestra otra persona.
      interaction: { mode: "index", axis: "y", intersect: false },
      plugins: {
        legend: { position: "bottom", labels: { boxWidth: 10, boxHeight: 10, padding: 12 } },
        tooltip: {
          filter: (item) => item.parsed.x > 0,
          callbacks: {
            footer: (items) => {
              const total = items.reduce((acc, item) => acc + item.parsed.x, 0);
              return `Total: ${total}`;
            },
          },
        },
      },
      scales: {
        x: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
        y: {
          stacked: true,
          ticks: {
            autoSkip: false,
            // Nombres largos recortados en el eje; el tooltip muestra el nombre completo.
            callback(value) {
              const label = String(this.getLabelForValue(value) || "");
              return label.length > 22 ? `${label.slice(0, 21)}…` : label;
            },
          },
        },
      },
    }),
    []
  );

  const antiguedadOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y} solicitud${ctx.parsed.y === 1 ? "" : "es"} esperando` } },
      },
      scales: {
        x: { ticks: { maxRotation: 0 }, title: { display: true, text: "Días esperando aprobación" } },
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    }),
    []
  );

  const horizontalBarOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
    }),
    []
  );

  const barData = useMemo(() => {
    if (!data) return null;
    return {
      labels: ["Pendiente", "Aprobado", "Rechazado"],
      datasets: [
        {
          data: [data.pendientes, data.aprobados, data.rechazados],
          backgroundColor: ["#facc15", "#22c55e", "#ef4444"],
          borderRadius: 8,
        },
      ],
    };
  }, [data]);

  // Mismos colores que "Estado de las solicitudes" para leerlos juntos.
  const estadoPorTipoData = useMemo(() => {
    const rows = data?.estadoPorTipo || [];
    if (!rows.length) return null;
    return {
      labels: rows.map((x) => x.tipo),
      datasets: [
        { label: "Aprobadas", data: rows.map((x) => x.aprobadas), backgroundColor: "#22c55e" },
        { label: "Pendientes", data: rows.map((x) => x.pendientes), backgroundColor: "#facc15" },
        { label: "Rechazadas", data: rows.map((x) => x.rechazadas), backgroundColor: "#ef4444" },
      ],
    };
  }, [data]);

  const destinosCriticosData = useMemo(() => {
    if (!data?.rankingDestinosCriticos?.length) return null;
    const destinos = data.rankingDestinosCriticos.slice(0, MAX_DESTINOS_CHART);
    return {
      labels: destinos.map((x) =>
        String(x.destino || "")
          .split("/")
          .map((part) => part.trim())
      ),
      datasets: [
        {
          data: destinos.map((x) => x.total),
          backgroundColor: "#ef4444",
          borderRadius: 8,
        },
      ],
    };
  }, [data]);

  const antiguedadData = useMemo(() => {
    const rows = data?.antiguedadPendientes || [];
    if (!rows.some((x) => x.total > 0)) return null;
    return {
      labels: rows.map((x) => x.label),
      datasets: [
        {
          data: rows.map((x) => x.total),
          backgroundColor: ANTIGUEDAD_COLORS,
          borderRadius: 8,
        },
      ],
    };
  }, [data]);

  const rankingTiposData = useMemo(() => {
    if (!data?.rankingTiposSalida?.length) return null;
    return {
      labels: data.rankingTiposSalida.map((x) => x.tipo),
      datasets: [
        {
          data: data.rankingTiposSalida.map((x) => x.total),
          backgroundColor: "#f59e0b",
          borderRadius: 8,
        },
      ],
    };
  }, [data]);

  const rankingUsuariosData = useMemo(() => {
    if (!data?.rankingUsuariosSalida?.length) return null;
    return {
      labels: data.rankingUsuariosSalida.map((x) => x.usuario),
      datasets: [
        {
          label: "Otras salidas",
          data: data.rankingUsuariosSalida.map((x) => x.otras ?? x.total ?? 0),
          backgroundColor: "#0ea5e9",
          borderRadius: 8,
        },
        {
          label: "No Pude Picar",
          data: data.rankingUsuariosSalida.map((x) => x.olvidePicar ?? 0),
          backgroundColor: "#f59e0b",
          borderRadius: 8,
        },
      ],
    };
  }, [data]);

  const calidadData = useMemo(() => {
    const c = data?.calidadRegistroCampo;
    if (!c?.total) return null;
    return {
      labels: ["Con hora de fin", "Sin hora de fin"],
      datasets: [
        {
          data: [c.conHoraFin, c.total - c.conHoraFin],
          backgroundColor: ["#22c55e", "#ef4444"],
          borderRadius: 8,
        },
      ],
    };
  }, [data]);

  const diaSemanaData = useMemo(() => {
    const rows = data?.salidasPorDiaSemana || [];
    if (!rows.some((x) => x.campo + x.otros > 0)) return null;
    return {
      labels: rows.map((x) => x.dia),
      datasets: [
        {
          label: "Comisión de servicio",
          data: rows.map((x) => x.campo),
          backgroundColor: "#0ea5e9",
          borderRadius: 6,
        },
        {
          label: "Otros permisos",
          data: rows.map((x) => x.otros),
          backgroundColor: "#a78bfa",
          borderRadius: 6,
        },
      ],
    };
  }, [data]);

  const responsablesData = useMemo(() => {
    const rows = data?.solicitudesPorResponsable || [];
    if (!rows.length) return null;
    // Con un responsable elegido, las demás barras se atenúan para resaltarlo.
    const color = (hex) =>
      rows.map((x) => (!responsableSel || x.responsable === responsableSel ? hex : `${hex}40`));
    return {
      labels: rows.map((x) => x.responsable),
      datasets: [
        { label: "Aprobadas", data: rows.map((x) => x.aprobadas), backgroundColor: color("#22c55e") },
        { label: "Pendientes", data: rows.map((x) => x.pendientes), backgroundColor: color("#facc15") },
        { label: "Rechazadas", data: rows.map((x) => x.rechazadas), backgroundColor: color("#ef4444") },
      ],
    };
  }, [data, responsableSel]);

  const responsablesOptions = useMemo(
    () => ({
      ...estadoApiladoOptions,
      onClick: (_evt, elements, chart) => {
        if (!elements.length) return;
        const nombre = chart.data.labels[elements[0].index];
        // Un segundo clic sobre el mismo responsable quita la selección.
        setResponsableSel((prev) => (prev === nombre ? null : nombre));
      },
      onHover: (evt, elements) => {
        const target = evt.native?.target;
        if (target) target.style.cursor = elements.length ? "pointer" : "default";
      },
    }),
    [estadoApiladoOptions]
  );

  if (loading) return <DashboardSkeleton />;
  if (error) return <p className="dash-error">{error}</p>;
  if (!data) return null;

  const totalSolicitudes = data.pendientes + data.aprobados + data.rechazados;
  const aprobacionPct =
    totalSolicitudes > 0
      ? Number(((data.aprobados / totalSolicitudes) * 100).toFixed(1))
      : 0;
  const rechazoPct =
    totalSolicitudes > 0
      ? Number(((data.rechazados / totalSolicitudes) * 100).toFixed(1))
      : 0;
  const pendientePct =
    totalSolicitudes > 0
      ? Number(((data.pendientes / totalSolicitudes) * 100).toFixed(1))
      : 0;
  const sinHoraFinPct = 100 - (data.campoHoraFinPct || 0);

  const riesgoLabel =
    sinHoraFinPct >= 20 || rechazoPct >= 20
      ? "Riesgo alto"
      : sinHoraFinPct >= 10 || rechazoPct >= 10
      ? "Riesgo medio"
      : "Riesgo controlado";
  const riesgoKey =
    riesgoLabel === "Riesgo alto"
      ? "bad"
      : riesgoLabel === "Riesgo medio"
      ? "warn"
      : "ok";

  const attention =
    data.pendientes > 0
      ? {
          key: "pending",
          count: data.pendientes,
          title: `solicitud${data.pendientes === 1 ? "" : "es"} pendiente${data.pendientes === 1 ? "" : "s"}`,
          detail: "Revisa las solicitudes que aún esperan una decisión.",
        }
      : riesgoKey !== "ok"
      ? {
          key: riesgoKey,
          title: riesgoLabel,
          detail: "Hay registros de campo que requieren seguimiento.",
        }
      : {
          key: "ok",
          title: "Todo está al día",
          detail: "No hay solicitudes pendientes para el período seleccionado.",
        };

  const statusSummary =
    totalSolicitudes === 0
      ? "Aún no hay solicitudes registradas en este período."
      : `${data.aprobados} aprobadas, ${data.pendientes} pendientes y ${data.rechazados} rechazadas.`;

  const calidad = data.calidadRegistroCampo || { total: 0, conHoraFin: 0 };
  const calidadSummary =
    calidad.total === 0
      ? "Sin comisiones de servicio en este período."
      : `${Math.round((calidad.conHoraFin / calidad.total) * 100)}% de las comisiones registraron su hora de fin.`;

  const responsables = data.solicitudesPorResponsable || [];
  const responsableActual =
    responsables.find((x) => x.responsable === responsableSel) || responsables[0];
  const responsablesSummary = responsableActual
    ? `${responsableActual.responsable} recibió ${responsableActual.total} solicitud${responsableActual.total === 1 ? "" : "es"}: ` +
      `${responsableActual.aprobadas} aprobada${responsableActual.aprobadas === 1 ? "" : "s"}, ` +
      `${responsableActual.pendientes} por aprobar y ` +
      `${responsableActual.rechazadas} rechazada${responsableActual.rechazadas === 1 ? "" : "s"}.`
    : "Sin solicitudes en este período.";
  const responsablesHint = responsables.length > 1 && !responsableSel
    ? "Haz clic en un responsable para ver su detalle."
    : null;

  const diaPico = (data.salidasPorDiaSemana || []).reduce(
    (best, x) => (x.campo + x.otros > best.total ? { dia: x.dia, total: x.campo + x.otros } : best),
    { dia: null, total: 0 }
  );
  const diaSemanaSummary = diaPico.dia
    ? `El día con más salidas es ${DIAS_LARGOS[diaPico.dia]} (${diaPico.total}).`
    : "Sin salidas en este período.";

  // Tipo con mayor % de rechazo (solo entre los que tienen algún rechazo).
  const mayorRechazo = (data.estadoPorTipo || []).reduce((best, x) => {
    const total = x.aprobadas + x.pendientes + x.rechazadas;
    const pct = total > 0 ? x.rechazadas / total : 0;
    return x.rechazadas > 0 && pct > best.pct ? { tipo: x.tipo, pct } : best;
  }, { tipo: null, pct: 0 });
  const estadoPorTipoSummary = !data.estadoPorTipo?.length
    ? "Sin solicitudes en este período."
    : mayorRechazo.tipo
      ? `Mayor rechazo: ${mayorRechazo.tipo} (${Math.round(mayorRechazo.pct * 100)}%).`
      : "Ningún tipo de salida tiene rechazos.";

  const pendientesAtrasadas = (data.antiguedadPendientes || [])
    .filter((x) => x.key === "quincena" || x.key === "atrasada")
    .reduce((acc, x) => acc + x.total, 0);
  const diasMasAntigua = data.pendienteMasAntiguaDias || 0;
  const antiguedadSummary =
    data.pendientes === 0
      ? "No hay solicitudes esperando aprobación."
      : `${data.pendientes} sin aprobar. La más antigua espera desde hace ${diasMasAntigua} día${diasMasAntigua === 1 ? "" : "s"}` +
        (pendientesAtrasadas > 0
          ? `; ${pendientesAtrasadas} ya pasa${pendientesAtrasadas === 1 ? "" : "n"} los 7 días.`
          : ".");

  const mesLabel = MESES.find((m) => m.v === monthPermisos)?.t || "Todos";
  const years = Array.from({ length: 5 }).map((_, i) => new Date().getFullYear() - i);

  const renderPeriodControls = () => (
    <div className="filters-inline">
      <div className="period-field">
        <span className="period-field__label">Mes</span>
        <div className="period-field__control">
          <select
            className="period-select"
            value={monthPermisos}
            onChange={(e) => setMonthPermisos(Number(e.target.value))}
            aria-label="Seleccionar mes"
          >
            {MESES.map((m) => (
              <option key={m.v} value={m.v}>
                {m.t}
              </option>
            ))}
          </select>
          <span className="period-caret">⌄</span>
        </div>
      </div>

      <div className="period-field">
        <span className="period-field__label">Año</span>
        <div className="period-field__control">
          <select
            className="period-select"
            value={yearPermisos}
            onChange={(e) => setYearPermisos(Number(e.target.value))}
            aria-label="Seleccionar año"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <span className="period-caret">⌄</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page dashboard-page dashboard-page--loaded">
      <div className="dash-hero">
        <div className="dash-hero__content">
          <p className="dash-eyebrow">{role === "manager" ? "Tu equipo" : "Resumen operativo"}</p>
          <h1 className="dash-title">Hola{user?.displayName ? `, ${user.displayName.split(" ")[0]}` : ""}</h1>
          <p className="dash-subtitle">
            Así va el control de salidas a campo en {mesLabel.toLowerCase()} de {yearPermisos}.
          </p>
        </div>

        <div className="dash-hero__actions">
          <OnboardingTour steps={DASHBOARD_TOUR} storageKey={`onboarding:dashboard:${user?.uid || "default"}`} />
          {renderPeriodControls()}
        </div>
      </div>

      <section className={`dash-attention dash-attention--${attention.key}`} aria-live="polite">
        <div className="dash-attention__content">
          <span className="dash-attention__eyebrow">{attention.key === "ok" ? "Estado del período" : "Requiere atención"}</span>
          <h2>
            {attention.count != null && (
              <>
                <CountUp value={attention.count} />{" "}
              </>
            )}
            {attention.title}
          </h2>
          <p>{attention.detail}</p>
          {attention.key === "ok" ? (
            <span className="dash-semaforo dash-semaforo--hero ok">Controlado</span>
          ) : (
            <Link className="dash-attention__action" to="/asistencia-campo">
              Revisar solicitudes
            </Link>
          )}
        </div>
        <img className="dash-attention__illustration" src={attentionIllustration} alt="" aria-hidden="true" />
      </section>

      {ROLES_BOLETAS.includes(role) && (
        <section className="dash-block">
          <div className="dash-block-title-row">
            <h2 className="dash-block-title">Boletas de pago</h2>
          </div>
          <BoletasResumenCard month={monthPermisos} year={yearPermisos} />
        </section>
      )}

      <section className="dash-block">
        <div className="dash-block-title-row">
          <h2 className="dash-block-title">Resumen del período</h2>
        </div>

        <div className="dash-kpi-grid">
          <div className="dash-kpi-card dash-kpi-card--total">
            <span className="dash-kpi-label">Solicitudes del periodo</span>
            <strong className="dash-kpi-value"><CountUp value={totalSolicitudes} /></strong>
            <span className="dash-kpi-note">Registradas en el período</span>
          </div>
          <div className="dash-kpi-card dash-kpi-card--approved">
            <span className="dash-kpi-label">Aprobadas</span>
            <strong className="dash-kpi-value"><CountUp value={aprobacionPct} decimals={Number.isInteger(aprobacionPct) ? 0 : 1} suffix="%" /></strong>
            <span className="dash-kpi-note">{data.aprobados} aprobadas</span>
          </div>
          <div className="dash-kpi-card dash-kpi-card--rejected">
            <span className="dash-kpi-label">Rechazadas</span>
            <strong className="dash-kpi-value"><CountUp value={rechazoPct} decimals={Number.isInteger(rechazoPct) ? 0 : 1} suffix="%" /></strong>
            <span className="dash-kpi-note">{data.rechazados} rechazadas</span>
          </div>
          <div className="dash-kpi-card dash-kpi-card--pending">
            <span className="dash-kpi-label">Pendientes</span>
            <strong className="dash-kpi-value"><CountUp value={pendientePct} decimals={Number.isInteger(pendientePct) ? 0 : 1} suffix="%" /></strong>
            <span className="dash-kpi-note">{data.pendientes} por gestionar</span>
          </div>
          <div className="dash-kpi-card">
            <span className="dash-kpi-label">Registros de campo completos</span>
            <strong className="dash-kpi-value">
              <CountUp value={data.campoHoraFinPct || 0} decimals={1} suffix="%" />
            </strong>
            <span className="dash-mini">
              {(data.campoConHoraFin || 0)} de {(data.campoPendientesTotal || 0)}
            </span>
            <span className={`dash-semaforo ${riesgoKey}`}>{riesgoLabel}</span>
          </div>
        </div>

        <div className="dash-chart-grid">
          <div className="dash-section" data-onboarding="status-chart">
            <div className="dash-section-header">
              <div>
                <h3>Estado de las solicitudes</h3>
                <p className="dash-section-summary">{statusSummary}</p>
              </div>
            </div>
            <div className="dash-chart-container">
              {barData && <Bar data={barData} options={barOptions} />}
            </div>
          </div>

          <div className="dash-section" data-onboarding="status-by-type-chart">
            <div className="dash-section-header">
              <div>
                <h3>Estado por tipo de salida</h3>
                <p className="dash-section-summary">{estadoPorTipoSummary}</p>
              </div>
            </div>
            <div className="dash-chart-container">
              {estadoPorTipoData ? (
                <Bar data={estadoPorTipoData} options={estadoApiladoOptions} />
              ) : (
                <p className="dash-empty">Sin datos de tipos de salida</p>
              )}
            </div>
          </div>

          <div className="dash-section" data-onboarding="quality-chart">
            <div className="dash-section-header">
              <div>
                <h3>Calidad del registro de campo</h3>
                <p className="dash-section-summary">{calidadSummary}</p>
              </div>
            </div>
            <div className="dash-chart-container">
              {calidadData ? (
                <Bar data={calidadData} options={calidadOptions} />
              ) : (
                <p className="dash-empty">Sin comisiones de servicio</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="dash-block">
        <div className="dash-block-title-row">
          <h2 className="dash-block-title">Quién y hacia dónde</h2>
        </div>

        <div className="dash-chart-grid">
          <div className="dash-section" data-onboarding="destinations-chart">
            <div className="dash-section-header">
              <h3>Destinos con más solicitudes</h3>
            </div>
            <div className="dash-chart-container">
              {destinosCriticosData ? (
                <Bar data={destinosCriticosData} options={destinosOptions} />
              ) : (
                <p className="dash-empty">Sin datos de destinos</p>
              )}
            </div>
          </div>

          <div className="dash-section" data-onboarding="people-chart">
            <div className="dash-section-header">
              <h3>Personas con más solicitudes</h3>
            </div>
            <div className="dash-chart-container">
              {rankingUsuariosData ? (
                <Bar data={rankingUsuariosData} options={userRankOptions} />
              ) : (
                <p className="dash-empty">Sin datos de ranking</p>
              )}
            </div>
          </div>

          <div className="dash-section" data-onboarding="managers-chart">
            <div className="dash-section-header">
              <div>
                <h3>Solicitudes recibidas por responsable</h3>
                <p className="dash-section-summary" aria-live="polite">{responsablesSummary}</p>
                {responsablesHint && <p className="dash-section-hint">{responsablesHint}</p>}
              </div>
            </div>
            <div className="dash-chart-container">
              {responsablesData ? (
                <Bar data={responsablesData} options={responsablesOptions} />
              ) : (
                <p className="dash-empty">Sin solicitudes en este período</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="dash-block">
        <div className="dash-block-title-row">
          <h2 className="dash-block-title">Tiempos y ritmo del período</h2>
        </div>

        <div className="dash-chart-grid">
          <div className="dash-section" data-onboarding="weekday-chart">
            <div className="dash-section-header">
              <div>
                <h3>Salidas por día de la semana</h3>
                <p className="dash-section-summary">{diaSemanaSummary}</p>
              </div>
            </div>
            <div className="dash-chart-container">
              {diaSemanaData ? (
                <Bar data={diaSemanaData} options={diaSemanaOptions} />
              ) : (
                <p className="dash-empty">Sin datos del período</p>
              )}
            </div>
          </div>

          <div className="dash-section" data-onboarding="types-rank-chart">
            <div className="dash-section-header">
              <h3>Tipos de salida más usados</h3>
            </div>
            <div className="dash-chart-container">
              {rankingTiposData ? (
                <Bar data={rankingTiposData} options={horizontalBarOptions} />
              ) : (
                <p className="dash-empty">Sin datos de ranking</p>
              )}
            </div>
          </div>

          <div className="dash-section" data-onboarding="aging-chart">
            <div className="dash-section-header">
              <div>
                <h3>¿Cuánto esperan las pendientes?</h3>
                <p className="dash-section-summary">{antiguedadSummary}</p>
              </div>
            </div>
            <div className="dash-chart-container">
              {antiguedadData ? (
                <Bar data={antiguedadData} options={antiguedadOptions} />
              ) : (
                <p className="dash-empty">Sin solicitudes pendientes</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
